<template>
	<div class="xs-12">
		<div class="flex xs-12">
			<div class="xs-5 pr1">
				<core-form-input
					name="transactionType"
					componentName="j-base-select"
					:options="options.transactionType"
					:label="$t('transaction_for')"
					v-model="transactionType"
					@input="onTransactionTypeChange"
				/>
			</div>
			<div class="xs-7">
				<core-form-input
					key="policy"
					v-if="
						transactionType === 'policy' || transactionType === 'endorsement'
					"
					:placeholder="$t('search_policy_number')"
					componentName="j-base-multi-select-with-flexible-value"
					class="fs3"
					name="policy"
					:label="$t('policy_number')"
					nameKey="issuerNumber"
					valueKey="id"
					:allowMultiselect="false"
					v-model="selectedPolicy"
					:options="policiesOptions"
					v-validate="{ required: transactionType === 'policy' }"
					:validationErrors="errors"
					@focus="onPolicySearch"
					@input="onPolicyInput"
					@search="onPolicySearch($event)"
				/>
				<core-form-input
					key="claim"
					v-if="transactionType === 'claim'"
					:placeholder="$t('search_claims_number')"
					componentName="j-base-multi-select-with-flexible-value"
					class="fs3"
					name="claim"
					:label="$t('claim_number')"
					nameKey="issuerNumber"
					valueKey="id"
					:allowMultiselect="false"
					v-model="selectedClaim"
					:options="claimsOptions"
					v-validate="{ required: transactionType === 'claim' }"
					:validationErrors="errors"
					@focus="onClaimSearch"
					@input="onClaimInput"
					@search="onClaimSearch($event)"
				/>
				<core-form-input
					key="proposal"
					v-if="transactionType === 'proposal'"
					:placeholder="$t('search_proposal_name')"
					componentName="j-base-multi-select-with-flexible-value"
					class="fs3"
					name="proposal"
					:label="$t('proposal_name')"
					nameKey="name"
					valueKey="id"
					:allowMultiselect="false"
					v-model="selectedProposal"
					:options="proposalOptions"
					v-validate="{ required: transactionType === 'proposal' }"
					:validationErrors="errors"
					@focus="onProposalSearch"
					@input="onProposalInput"
					@search="onProposalSearch($event)"
				/>
				<core-form-input
					key="endorsement"
					v-if="this.form.policyId && transactionType === 'endorsement'"
					:placeholder="$t('search_endorsement_date')"
					componentName="j-base-multi-select-with-flexible-value"
					class="fs3"
					name="endorsement"
					:label="$t('endorsement_date')"
					nameKey="endorsementDate"
					valueKey="id"
					:allowMultiselect="false"
					v-model="selectedEndorsement"
					:options="endorsementOptions"
					v-validate="{ required: transactionType === 'endorsement' }"
					:validationErrors="errors"
					@focus="onEndorsementSearch"
					@input="onEndorsementInput"
					@search="onEndorsementSearch($event)"
				/>
			</div>
		</div>

		<core-form-input
			class="mt6"
			componentName="j-base-input-text"
			name="transactionNumber"
			:label="$t('debitNoteNumber')"
			v-model="form.transactionNumber"
		/>

		<core-form-input
			class="mt6"
			componentName="j-base-input-number"
			name="amount"
			:label="$t('amount')"
			placeholder="100.50"
			v-validate="{ required: true }"
			:validationErrors="errors"
			v-model="form.amount"
		/>

		<core-form-input
			class="mt6"
			componentName="core-select-currency"
			name="currencyCode"
			:label="$t('currency')"
			v-validate="{ required: true }"
			:validationErrors="errors"
			v-model="form.currencyCode"
		/>

		<core-form-input
			class="mt6"
			componentName="j-base-select"
			:options="options.status"
			name="status"
			:label="$t('status')"
			v-validate="{ required: true }"
			:validationErrors="errors"
			v-model="form.status"
		/>

		<core-form-input
			class="mt6"
			componentName="j-base-select"
			:options="paymentMethodOptions"
			name="method"
			:label="$t('payment_method')"
			v-model="form.method"
		>
			<span slot="label" class="fs3 c6">({{ $t("optional") }})</span>
		</core-form-input>

		<core-form-input
			componentName="j-base-input-text"
			id="date"
			name="date"
			:label="`${$t('date')} (DD/MM/YYYY HH:mm:ss)`"
			placeholder="01/01/2019 18:06:44"
			:description="$t('transaction_date_time_description')"
			v-validate="'date_format:DD/MM/YYYY HH:mm:ss'"
			v-model="form.dateTime"
			:validationErrors="errors"
			class="mt6"
			size="small"
		>
			<span slot="label" class="fs3 c6">({{ $t("optional") }})</span>
		</core-form-input>

		<core-form-input
			componentName="j-base-input-text"
			id="postDate"
			name="postDate"
			:label="`${$t('post_date')}`"
			placeholder="01/01/2019 18:06:44"
			v-validate="'date_format:DD/MM/YYYY HH:mm:ss'"
			v-model="form.postDateTime"
			:validationErrors="errors"
			class="mt6"
			size="small"
		>
			<span slot="label" class="fs3 c6">({{ $t("optional") }})</span>
		</core-form-input>

		<core-form-input
			componentName="j-base-input-text"
			id="dueDateTime"
			name="dueDateTime"
			:label="`${$t('due_date')}`"
			placeholder="01/01/2019 18:06:44"
			v-validate="'date_format:DD/MM/YYYY HH:mm:ss'"
			v-model="form.dueDateTime"
			:validationErrors="errors"
			class="mt6"
			size="small"
		>
			<span slot="label" class="fs3 c6">({{ $t("optional") }})</span>
		</core-form-input>

		<!-- Slot passing up some methods -->
		<slot :onSubmit="onSubmit"></slot>
	</div>
</template>

<script>
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import debounce from "lodash.debounce"
import get from "lodash.get"
import api from "../api/"
import dayjs from "dayjs"

export default {
	components: { CoreFormInput },
	props: {
		initialPayload: {
			type: Object,
			required: false,
			default: () => ({})
		}
	},

	data() {
		return {
			transactionType: null,
			policiesOptions: [],
			selectedPolicy: null,
			selectedProposal: null,
			selectedEndorsement: null,
			proposalOptions: [],
			endorsementOptions: [],
			claimsOptions: [],
			selectedClaim: null,
			transactionObject: null,
			form: {
				proposalId: null,
				endorsementId: null,
				claimId: null,
				policyId: null,
				amount: "",
				transactionNumber: null,
				status: null,
				method: null,
				dateTime: null,
				postDateTime: null,
				dueDateTime: null,
				currencyCode: this.$store.state.settings.preferredCurrency
			},

			options: {
				status: [
					{ name: "UNDEFINED", value: "UNDEFINED" },
					{ name: "NOT_STARTED", value: "NOT_STARTED" },
					{ name: "APPROVED", value: "APPROVED" },
					{ name: "REJECTED", value: "REJECTED" },
					{ name: "TIMEOUT", value: "TIMEOUT" },
					{ name: "CANCELLED_BY_USER", value: "CANCELLED_BY_USER" },
					{ name: "REFUNDED", value: "REFUNDED" }
				],
				transactionType: [
					{ name: this.$t("policy"), value: "policy" },
					{ name: this.$t("claim"), value: "claim" },
					{ name: this.$t("proposal"), value: "proposal" },
					{ name: this.$t("endorsement"), value: "endorsement" }
				]
			}
		}
	},

	computed: {
		/**
		 * NOTE: Hack here, if tenant is asia or asia_uat return limited list
		 * This hack should be removed once we have settings for this in database
		 */
		paymentMethodOptions() {
			if (
				this.$store.state.tenantId === "asia" ||
				this.$store.state.tenantId === "asia_uat"
			)
				return [
					{ name: "CREDIT_CARD", value: "CREDIT_CARD" },
					{ name: "ATM", value: "ATM" },
					{ name: "BANK_TRANSFER", value: "BANK_TRANSFER" },
					{ name: "CHEQUE", value: "CHEQUE" }
				]
			else
				return [
					{ name: "CASH", value: "CASH" },
					{ name: "CREDIT_CARD", value: "CREDIT_CARD" },
					{ name: "GIRO", value: "GIRO" },
					{ name: "ATM", value: "ATM" },
					{
						name: "SELF_SERVICE_PAYMENT_MACHINE",
						value: "SELF_SERVICE_PAYMENT_MACHINE"
					},
					{ name: "BANK_TRANSFER", value: "BANK_TRANSFER" },
					{ name: "CHEQUE", value: "CHEQUE" }
				]
		}
	},

	methods: {
		/**
		 * Validate form and return payload of the form
		 */
		async onSubmit() {
			const isValid = await this.$validator.validate()
			if (!isValid) return
			this.$emit("submit", {
				...this.form,
				method: this.form.method ? this.form.method : null
			}) // make sure that empty string bcomes null as in APIs it's ENUMs and we have to send empty string
		},

		onPolicySearch: debounce(async function(searchTerm) {
			this.isSearching = true

			const res = await api.queryPolicies({
				limit: searchTerm ? 30 : null,
				where: { issuerNumber_contains: (searchTerm || "").toString() }
			})

			if (!res || res.errors || !get(res, "data.policies.totalCount")) {
				// no result
				this.policiesOptions = []
				this.isLoading = false
				return
			}

			this.policiesOptions = get(res, "data.policies.list")

			if (this.transactionType === "endorsement")
				this.policiesOptions = this.policiesOptions.filter(
					x => x.endorsements.length
				)
			this.isSearching = false
		}, 500),

		onPolicyInput() {
			this.form.policyId = this.selectedPolicy[0]
				? this.selectedPolicy[0].id
				: null
			if (!this.selectedPolicy.length) {
				this.endorsementOptions = []
				this.selectedEndorsement = null
				this.form.endorsementId = null
			}
		},

		onEndorsementSearch: debounce(async function(searchTerm) {
			this.isSearching = true

			const res = await api.queryPolicies({
				limit: searchTerm ? 30 : null
			})

			if (!res || res.errors || !get(res, "data.policies.totalCount")) {
				// no result
				this.endorsementOptions = []
				this.isLoading = false
				return
			}

			let optionsEndorsement = get(res, "data.policies.list").filter(
				x => x.id === this.form.policyId
			)[0].endorsements

			optionsEndorsement = optionsEndorsement.map(x => {
				x.endorsementDate = x.endorsementDate ? dayjs(x.endorsementDate).format("DD/MM/YYYY HH:mm:ss") : '---';
				return x;
			})

			if (searchTerm)
				optionsEndorsement = optionsEndorsement.filter(x =>
					x.endorsementDate.includes(searchTerm.trim())
				)

			this.endorsementOptions = optionsEndorsement
			this.isSearching = false
		}, 500),

		onEndorsementInput() {
			this.form.endorsementId = this.selectedEndorsement[0]
				? this.selectedEndorsement[0].id
				: null
		},

		onClaimSearch: debounce(async function(searchTerm) {
			this.isSearching = true

			const res = await api.queryClaims({
				limit: searchTerm ? 30 : null,
				where: { issuerNumber_contains: (searchTerm || "").toString() }
			})

			if (!res || res.errors || !get(res, "data.claims.totalCount")) {
				// no result
				this.claimsOptions = []
				this.isLoading = false
				return
			}

			this.claimsOptions = get(res, "data.claims.list")
			this.isSearching = false
		}, 500),

		onClaimInput() {
			this.form.claimId = this.selectedClaim[0]
				? this.selectedClaim[0].id
				: null
		},

		onProposalSearch: debounce(async function(searchTerm) {
			this.isSearching = true

			const res = await api.queryProposals({
				limit: searchTerm ? 30 : null,
				caseWhere: {
					proposal: {
						name_contains: searchTerm || null
					}
				},
				proposalsWhere: {
					name_contains: searchTerm || null
				}
			})

			if (!res || res.errors || !get(res, "data.proposals.list").length) {
				// no result
				this.proposalOptions = []
				this.isLoading = false
				return
			}

			const optionsProposal = get(res, "data.proposals.list").reduce(
				(acc, current) => {
					return [...acc, ...current.proposals]
				},
				[]
			)

			this.proposalOptions = optionsProposal
			this.isSearching = false
		}, 500),

		onProposalInput() {
			this.form.proposalId = this.selectedProposal[0]
				? this.selectedProposal[0].id
				: null
		},

		onTransactionTypeChange() {
			this.form.claimId = null
			this.form.policyId = null
			this.form.proposalId = null
			this.form.endorsementId = null
			this.claimsOptions = []
			this.policiesOptions = []
			this.proposalOptions = []
			this.endorsementOptions = []

			this.selectedClaim = null
			this.selectedPolicy = null
			this.selectedProposal = null
			this.selectedEndorsement = null
		}
	},

	watch: {
		/**
		 * When initial payload changes, upsert this.form
		 */
		initialPayload: {
			handler(newValue) {
				if (newValue.object) {
					const object = JSON.parse(newValue.object)
					this.transactionType = object.__typename

					if (this.transactionType === "policy") {
						this.form.policyId = object.id
						this.selectedPolicy = [
							{ id: object.id, issuerNumber: object.issuerNumber || '---'}
						]
					} else if (this.transactionType === "claim") {
						this.form.claimId = object.id
						this.selectedClaim = [
							{ id: object.id, issuerNumber: object.issuerNumber || '---' }
						]
					} else if (this.transactionType === "proposal") {
						this.form.proposalId = object.id
						this.selectedProposal = [{ id: object.id, name: object.name || '---' }]
					} else if (this.transactionType === "endorsement") {
						this.policiesOptions = [
							{
								issuerNumber: object.policy.issuerNumber,
								id: object.policy.id
							}
						]
						this.form.policyId = object.policy.id
						this.selectedPolicy = [
							{
								id: object.policy.id,
								issuerNumber: object.policy.issuerNumber
							}
						]
						this.form.endorsementId = object.id
						this.selectedEndorsement = [{ id: object.id, endorsementDate: object.endorsementDate ? dayjs(object.endorsementDate).format("DD/MM/YYYY HH:mm:ss") : '---' }]
					}
				}

				delete newValue.object

				this.form = { ...this.form, ...newValue }
			},
			immediate: true,
			deep: true
		}
	}
}
</script>
